import React, { useCallback, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { Button, CheckBox, TextBox } from 'devextreme-react';
import Guid from '../../models/Guid';
import './popup-fichiers-joints.css';

export interface IFichierRoute {
  id: string;
  chemin: string;
  anglais: boolean;
}

interface IPopupFichiersJointsProps {
  onClose: () => void;
  fichiers?: IFichierRoute[];
  onConfirm?: (fichiers: IFichierRoute[]) => void;
  readOnly?: boolean;
}

export default function PopupFichiersJoints({ onClose, fichiers = [], onConfirm, readOnly = false }: IPopupFichiersJointsProps) {
  const [rows, setRows] = useState<IFichierRoute[]>(() => fichiers.map((f) => ({ ...f })));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const browseRowIdRef = useRef<string | null>(null);

  const openFilePicker = useCallback((rowId: string | null) => {
    browseRowIdRef.current = rowId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const chemin = file.name;
    const targetRowId = browseRowIdRef.current;

    setRows((prev) => {
      if (targetRowId) {
        return prev.map((row) => (row.id === targetRowId ? { ...row, chemin } : row));
      }
      return [...prev, { id: Guid.newTemporary().toString(), chemin, anglais: false }];
    });
  }, []);

  const updateChemin = useCallback((id: string, chemin: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, chemin } : row)));
  }, []);

  const updateAnglais = useCallback((id: string, anglais: boolean) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, anglais } : row)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const addEmptyRow = useCallback(() => {
    setRows((prev) => [...prev, { id: Guid.newTemporary().toString(), chemin: '', anglais: false }]);
  }, []);

  const okOptions = useMemo(() => ({
    text: 'OK',
    onClick: () => {
      if (!readOnly) {
        const cleaned = rows.filter((row) => row.chemin.trim().length > 0);
        onConfirm?.(cleaned);
      }
      onClose();
    },
  }), [rows, onConfirm, onClose, readOnly]);

  const annulerOptions = useMemo(() => ({
    text: 'Annuler',
    onClick: onClose,
  }), [onClose]);

  const renderCheminCell = useCallback((cellData: any) => {
    const row = cellData.data as IFichierRoute;
    if (readOnly) {
      return <span className="fichier-route-chemin-text">{row.chemin}</span>;
    }
    return (
      <div className="fichier-route-chemin">
        <TextBox
          value={row.chemin}
          stylingMode="outlined"
          onValueChanged={(e) => {
            if (!e.event) return;
            updateChemin(row.id, e.value ?? '');
          }}
        />
        <Button
          text="…"
          width={36}
          stylingMode="outlined"
          onClick={() => openFilePicker(row.id)}
        />
        <Button
          icon="close"
          width={36}
          stylingMode="outlined"
          type="danger"
          onClick={() => removeRow(row.id)}
        />
      </div>
    );
  }, [readOnly, openFilePicker, updateChemin, removeRow]);

  const renderAnglaisCell = useCallback((cellData: any) => {
    const row = cellData.data as IFichierRoute;
    return (
      <CheckBox
        value={row.anglais}
        readOnly={readOnly}
        disabled={readOnly}
        onValueChanged={(e) => {
          if (readOnly || !e.event) return;
          updateAnglais(row.id, !!e.value);
        }}
      />
    );
  }, [readOnly, updateAnglais]);

  return (
    <Popup
      visible={true}
      onHiding={onClose}
      dragEnabled={true}
      hideOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="Liste des fichiers joints"
      container=".dx-viewport"
      width={700}
      height={420}
    >
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept="*"
        onChange={handleFileSelected}
      />
      <div className="popup-fichiers-joints">
        {!readOnly ? (
          <div className="popup-fichiers-joints__toolbar">
            <Button text="Ajouter…" stylingMode="contained" onClick={() => openFilePicker(null)} />
            <Button text="Nouvelle ligne" stylingMode="outlined" onClick={addEmptyRow} />
          </div>
        ) : null}
        <DataGrid
          className="popup-fichiers-joints__grid"
          dataSource={rows}
          keyExpr="id"
          showBorders={true}
          showRowLines={true}
          showColumnLines={true}
          allowColumnResizing={true}
          columnAutoWidth={false}
          height="100%"
          noDataText="Aucun fichier"
        >
          <Editing allowUpdating={false} allowDeleting={false} allowAdding={false} />
          <Column
            dataField="chemin"
            caption="Chemin du fichier"
            cellRender={renderCheminCell}
          />
          <Column
            dataField="anglais"
            caption="Anglais"
            width={80}
            alignment="center"
            cellRender={renderAnglaisCell}
          />
        </DataGrid>
      </div>
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={okOptions}
      />
      {!readOnly ? (
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={annulerOptions}
        />
      ) : null}
    </Popup>
  );
}
